import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useCookies } from "react-cookie";
import { SiGooglescholar, SiPubmed, SiResearchgate, SiScopus } from "react-icons/si";
import { Link } from "react-router-dom";
import PropTypes from "prop-types"; // Added import
import FollowButton from "./FollowButton";
import Loading from "./Loading";
import MissingError from "./MissingError";

const queryClient = new QueryClient();

export default function ProfileCard({ id }) {
  return (
    <QueryClientProvider client={queryClient}>
      <App id={id} />
    </QueryClientProvider>
  );
}

function App({ id }) {
  const [cookies] = useCookies([]);

  const { isLoading, error, data } = useQuery({
    queryKey: ["authorData"],
    queryFn: () =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/adminPHIS/users`, {
        credentials: "include",
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: cookies["plt"],
        },
      }).then((res) => res.json()),
    retry: false,
    refetchOnWindowFocus: true,
  });

  if (isLoading) return <Loading />;
  if (error) return <MissingError />;

  return (
    <article className="bg-white shadow-md rounded-lg w-full max-w-2xl mx-auto p-6">
      <div className="flex flex-col items-center">
        {/* Profile Picture */}
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${data.user.profile_picture}`}
          alt={`${data.user.firstname}'s profile picture`}
          className="w-24 h-24 rounded-full border-2 border-gray-200 object-cover hover:shadow-lg transition-shadow duration-200"
          aria-label="User profile picture"
        />

        {/* Name and Role */}
        <h2 className="mt-4 text-2xl font-semibold text-gray-900">
          {data.user.firstname} {data.user.lastname}
        </h2>
        {data.author ? (
          <span className="mt-1 text-sm font-medium text-blue-900">
            {data.application.ace}
          </span>
        ) : (
          <span className="mt-1 px-3 py-1 bg-blue-900 text-white text-sm font-medium rounded-full">
            {data.user.role === "P" ? "Public User" : "Admin"}
          </span>
        )}

        {/* Bio */}
        <p className="mt-3 text-sm text-gray-600 text-center max-w-md">
          {data.user.about ? (
            data.user.about
          ) : (
            <>
              No bio found. Tell people something about yourself.{" "}
              <Link
                to="/phis/user/settings"
                className="text-blue-600 hover:text-blue-800 font-medium underline"
                aria-label="Navigate to settings to add a bio"
              >
                Go to Settings
              </Link>
            </>
          )}
        </p>

        {/* Author Stats and Social Links */}
        {data.author && (
          <>
            {/* Stats */}
            <div className="mt-4 w-full flex justify-center space-x-8 text-sm text-gray-600">
              <div className="flex flex-col items-center">
                <span className="font-medium">Followers</span>
                <span className="text-blue-900 font-bold">
                  {data.application.number_followers}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="font-medium">Following</span>
                <span className="text-blue-900 font-bold">0</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-4 flex justify-center space-x-4">
              {data.application.gs && (
                <a
                  href={data.application.gs}
                  className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors duration-200"
                  aria-label="Google Scholar profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SiGooglescholar className="h-5 w-5" />
                </a>
              )}
              {data.application.rg && (
                <a
                  href={data.application.rg}
                  className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors duration-200"
                  aria-label="ResearchGate profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SiResearchgate className="h-5 w-5" />
                </a>
              )}
              {data.application.sc && (
                <a
                  href={data.application.sc}
                  className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors duration-200"
                  aria-label="Scopus profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SiScopus className="h-5 w-5" />
                </a>
              )}
              {data.application.pb && (
                <a
                  href={data.application.pb}
                  className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors duration-200"
                  aria-label="PubMed profile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SiPubmed className="h-5 w-5" />
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  );
}

ProfileCard.propTypes = {
  id: PropTypes.string.isRequired,
};

App.propTypes = {
  id: PropTypes.string.isRequired,
};